import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonContent, IonModal, IonSpinner } from '@ionic/react';
import { IRecordingContext } from './RecordingEmptyState';
import { isAutomatedTests } from '../utils/isAutomatedTests';

const useStyles = createUseStyles({
    ionContent: {
        // 0px, not unitless 0: these feed Ionic's inner-scroll padding calc(), which a unitless 0 invalidates
        '--padding-top': '0px',
        '--padding-bottom': '0px',
        height: '100%',
        display: 'flex'
    },

    contentWrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 16px'
    },

    consentModalContent: {
        padding: '20px 0'
    },

    section: {
        marginBottom: 32,

        '& h3': {
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 16,
            color: 'var(--ion-color-dark)'
        }
    },

    benefitItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 12,
        gap: 12
    },

    permissionItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 12,
        gap: 12
    },

    icon: {
        fontSize: 20,
        lineHeight: 1,
        flexShrink: 0,
        backgroundColor: 'rgba(17, 120, 157, 0.1)',
        borderRadius: 8,
        padding: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    itemText: {
        fontSize: 16,
        lineHeight: 1.4,
        color: 'var(--ion-color-dark)'
    },

    consentModalActions: {
        marginTop: 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        maxWidth: 280,
        margin: '40px auto 0'
    },

    customTitle: {
        fontSize: 20,
        fontWeight: 500,
        color: 'var(--ion-color-dark)',
        textAlign: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        lineHeight: 1.3,
        padding: '16px 16px 16px 16px',
        margin: 0
    }
});

interface IConsentModalProps {
    isOpen: boolean;
    context: IRecordingContext;

    // Set once the user has accepted and the mic is being acquired. The modal deliberately stays
    // up for this: it is the only thing telling the user not to speak yet, and dismissing it early
    // reads as "go ahead" while the first second of audio would still be lost.
    isBusy: boolean;

    onAccept: () => void;
    onDecline: () => void;
    onDidDismiss?: () => void;
}

export const RecordingConsentModal: FC<IConsentModalProps> = ({
    isOpen,
    context,
    isBusy,
    onAccept,
    onDecline,
    onDidDismiss
}) => {
    const classes = useStyles();

    const getContextSpecificPermissionText = (contextType: string | undefined) => {
        switch (contextType) {
            case 'at-appointment':
                return <><b>Get permission to record</b>: "Is it okay if I record for my notes?"</>;
            default:
                return <>If others are there, <b>get permission</b>: "Is it okay if I record for my notes?"</>;
        }
    };

    return (
        <IonModal
            isOpen={isOpen}
            backdropDismiss={!isBusy}
            data-id="recording-consent-modal"
            onDidDismiss={onDidDismiss}
            animated={!isAutomatedTests()}
        >
            <IonContent className={classes.ionContent}>
                <div className={classes.contentWrapper}>
                    <div className={classes.customTitle} data-type="consent-modal-title">{context.consentHeader}</div>
                    <div className={classes.consentModalContent}>
                        <div className={classes.section}>
                            <h3>What You'll Get</h3>
                            <div className={classes.benefitItem}>
                                <span className={classes.icon}>✨</span>
                                <span className={classes.itemText}>A <b>clear summary</b> with key next steps</span>
                            </div>
                            <div className={classes.benefitItem}>
                                <span className={classes.icon}>🔒</span>
                                <span className={classes.itemText}>A <b>private note</b> you control + <b>share</b></span>
                            </div>
                        </div>

                        <div className={classes.section}>
                            <h3>Before You Start</h3>
                            <div className={classes.permissionItem}>
                                <span className={classes.icon}>🙋</span>
                                <span className={classes.itemText}>{getContextSpecificPermissionText(context.type)}</span>
                            </div>
                        </div>

                        <div className={classes.consentModalActions}>
                            <IonButton
                                fill="clear"
                                color="primary"
                                disabled={isBusy}
                                onClick={onDecline}
                                data-id="consent-decline-button"
                            >
                                No, go back
                            </IonButton>
                            <IonButton
                                expand="block"
                                color="primary"
                                disabled={isBusy}
                                onClick={onAccept}
                                data-id="consent-accept-button"
                            >
                                {isBusy ? <IonSpinner name="dots" style={{height: 20}} /> : 'Yes, Record'}
                            </IonButton>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonModal>
    );
};
